import { computed, h, onBeforeUnmount, onMounted } from 'vue';
import { NButton } from 'naive-ui';
import { usePlanRunStore } from '@/stores/trajectory/plan-run-store';
import { useTransportVehicleStore } from '@/stores/transport-vehicle/transport-vehicle-store';
import { useAuthStore } from '@/stores/authentication-store';
import { useMessageHandler } from '@/helpers/error/useMessageHandler';
import { giveSwimlaneItemHeight, maximumTransportLayer } from '@/helpers/day-planner-helper';
import { PlanRunStatus } from '@/constants/plan-run-status';
import { PlanRunFailureProfile } from '@/constants/plan-failure-reason';
import { planRunReference } from '@/helpers/plan-run-reference';
import type { PlanRun } from '@/models/plan-run/plan-run';
import type { TransportVehicleSchedule } from '@/models/transport-vehicle/transport-vehicle-schedule';

/**
 * Welke rijen van de dagplanner een "Automatisch plannen..."-placeholder krijgen, en hoe hoog die rijen
 * dan worden.
 *
 * De vergelijking tussen de rundag en de dag die de dagplanner toont staat hier en niet in de
 * plan-run-store: die store wordt ook op de trajectenlijst gebruikt, die geen dagplanner heeft, en zou
 * daarmee afhankelijk worden van weergavestate van een andere pagina. Hier staat de koppeling precies
 * één keer, en de drie kolomcomponenten houden een one-liner.
 */
export function usePlanRunPlaceholder() {
  const planRunStore = usePlanRunStore();
  const transportVehicleStore = useTransportVehicleStore();

  const cssVar = (val: string) => parseFloat(getComputedStyle(document.body).getPropertyValue(val));

  /** De verticale stap tussen twee gestapelde transportkaarten (kaarten cascaderen dus over elkaar). */
  const layerStep = () => cssVar('--dis-shape-height-4_0x');
  /** De volle hoogte van een transportkaart, en daarmee ook van de placeholder. */
  const cardHeight = () => cssVar('--dis-shape-height-7_0x');
  /**
   * Tussenruimte onder de stapel. Bewust een hele stap en niet de helft: een transportkaart krijgt er in
   * een rij met gemengde lagen 2px rand omheen, en die mag deze ruimte niet opeten.
   */
  const cardGap = () => cssVar('--dis-space-1_0x');

  const showsRunDay = computed(() =>
    planRunStore.activeRunStartDay !== null &&
    planRunStore.activeRunStartDay === transportVehicleStore.transportVehicleScheduleRequest.transportStartDate);

  const placeholderVehicleIds = computed<Set<number>>(() =>
    showsRunDay.value ? planRunStore.activeRunVehicleIds : new Set<number>());

  const hasPlaceholder = (transportVehicleId: number) => placeholderVehicleIds.value.has(transportVehicleId);

  /**
   * De ruimte die de placeholder onder de bestaande transporten nodig heeft: een volle kaart plus een
   * tussenruimte. Transportkaarten mogen over elkaar cascaderen, maar een laadstaat die half onder een
   * echte kaart verdwijnt leest als een fout — die zetten we er dus helemaal onder.
   */
  const placeholderExtraHeight = (schedule: TransportVehicleSchedule) =>
    hasPlaceholder(schedule.id) && (schedule.transports?.length ?? 0) > 0
      ? cardHeight() + cardGap()
      : 0;

  const placeholderOffsetFor = (schedule: TransportVehicleSchedule) => {
    if (!hasPlaceholder(schedule.id) || (schedule.transports?.length ?? 0) === 0) {
      return 0;
    }

    // Onderkant van de laatste kaart in de stapel, plus de tussenruimte.
    return maximumTransportLayer(schedule.transports) * layerStep() + cardHeight() + cardGap();
  };

  const swimlaneHeight = (schedule: TransportVehicleSchedule) =>
    giveSwimlaneItemHeight(layerStep(), schedule.transports ?? [], placeholderExtraHeight(schedule));

  return { hasPlaceholder, placeholderOffsetFor, swimlaneHeight };
}

/** Poll-intervallen. Actief korter dan de 3s waarmee de orkestratie APE zelf bevraagt is zinloos. */
const ACTIVE_POLL_MS = 5_000;
/** Zonder lopende run alleen om een run van een collega op te merken. */
const IDLE_POLL_MS = 20_000;

// Module-niveau, zodat meerdere afnemers (de dagplanner en de app-brede voortgangsweergave) dezelfde timer
// delen in plaats van er elk een eigen bij te zetten.
let subscribers = 0;
/**
 * Afnemers die de dagplanner in beeld hebben. Alleen dan wordt het dagplanner-overzicht na een run herladen:
 * de poller draait app-breed, en die opvraging gaat over de lijst-state van een andere pagina.
 */
let dayPlannerSubscribers = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
/** De run die wij zelf actief hebben zien staan; alleen daarover melden we de afronding. */
let trackedRunId: number | null = null;

const stopTimer = () => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
};

/**
 * De afronding melden zoals hij werkelijk is. Een geslaagde run betekent niet dat álles gepland is: APE kan
 * auto's niet kwijt kunnen (geen lier, geen ruimte) en een collega kan er tijdens de run zelf één hebben
 * ingepland. Die trajecten staan daarna nog op te plannen, en dat mag niet wegvallen achter een groene melding.
 *
 * De afloop is voor de planner die de run startte. De statusopvraging draait bij iedere ingelogde planner —
 * zij moeten zien dát er een run loopt, want die zet automatisch plannen voor de hele organisatie op slot —
 * maar de uitkomst is niet van hen. Zonder dit onderscheid krijgen drie planners alle drie dezelfde dialoog
 * over een run waar ze niets mee te maken hebben.
 */
const handleFinished = async (run: PlanRun, wasWatched: boolean) => {
  const planRunStore = usePlanRunStore();

  // De dagplanner hoort wél overal te verversen: de concept-ritten zijn er voor iedereen, ook voor wie de
  // run niet startte. Alleen wanneer de run zojuist voor onze ogen afliep — bij het inhalen van een oudere
  // run staan die ritten er allang.
  if (wasWatched && dayPlannerSubscribers > 0) {
    await useTransportVehicleStore().getTransportVehicleSchedule();
  }

  // Een afgebroken run is voor iedereen die hem zag lopen, niet alleen voor wie hem startte: hij verdween
  // zojuist van hun scherm en dat hoort niet zonder uitleg te gebeuren. Behalve voor wie zelf afbrak — die
  // kreeg zijn bevestiging al bij het indrukken en hoeft hem niet nog eens.
  const isCancelled = run.status === PlanRunStatus.Cancelled;
  const concernsMe = isCancelled ? !run.cancelledByMe : run.startedByMe;

  if (!concernsMe || planRunStore.hasAcknowledged(run.id)) {
    return;
  }

  // De meldingen-API komt van een provider; is die er nog niet, dan wachten we op de volgende ronde in
  // plaats van de afloop weg te gooien. Zonder deze controle verdwijnt de uitkomst van een run in een
  // uitzondering en ziet de planner nooit wat er gebeurd is.
  if (!useMessageHandler().notification) {
    return;
  }

  // Meteen vastleggen dat we de afloop presenteren, niet pas als de planner hem wegklikt: de inhaalslag
  // hieronder kijkt elke ronde of deze run al gezien is, en zonder dit zou dezelfde melding zich om de
  // twintig seconden herhalen.
  planRunStore.recordAcknowledged(run.id);

  // Afgebroken is geen mislukking: er ging niets stuk, iemand vroeg erom. Dus geen foutmelding en geen
  // "opnieuw proberen" — alleen de mededeling wie de run gestopt heeft.
  if (isCancelled) {
    notifyCancelled(run);
    return;
  }

  const unplannedCount = run.unplannedTrajectoryIds.length;
  const isEstimated = run.failureProfile === PlanRunFailureProfile.Estimated;

  // Alles wat uitleg per traject vraagt gaat naar de samenvattings-dialoog: een melding van drie regels kan
  // niet vertellen wélke auto's om welke reden bleven liggen, en juist dat heeft de planner nodig om ze zelf
  // op te pakken. Een run die volledig slaagde krijgt gewoon een melding — daar valt niets uit te leggen.
  if (run.status === PlanRunStatus.Completed && (unplannedCount > 0 || isEstimated)) {
    const summary = await planRunStore.fetchSummary(run.id, { show: true });

    // Lukt het ophalen niet, dan valt de terugkoppeling terug op de korte melding. Die vertelt niet wélke
    // trajecten bleven liggen, maar wél dát er iets bleef liggen — en dat is het enige dat echt niet mag
    // wegvallen.
    if (summary !== null) {
      return;
    }
  }

  if (run.status !== PlanRunStatus.Completed) {
    notifyFailed(run);
    return;
  }

  notifyFinished(run);
};

/**
 * De melding bij een run die een planner heeft afgebroken (werkitem 7405).
 *
 * Bewust een informatieve melding en geen waarschuwing: er is niets misgegaan en er hoeft niets hersteld te
 * worden. De naam van wie afbrak staat erin omdat dit vaak een collega is — zonder die naam leest het als een
 * run die zomaar van het scherm verdween. Ontbreekt de naam, dan valt de tekst terug op een variant zonder.
 */
const notifyCancelled = (run: PlanRun) => {
  const key = run.cancelledByName
    ? 'components.transport.dayplanner.plan_run.cancel.cancelled_by'
    : 'components.transport.dayplanner.plan_run.cancel.cancelled';

  useMessageHandler().showNotification(
    'components.transport.dayplanner.plan_run.cancel.stopped_title',
    `${key}_description`,
    'info',
    undefined,
    undefined,
    { values: { name: run.cancelledByName ?? '' } },
  );
};

/**
 * De melding bij een run die het niet haalde, met de tekst van zijn afhandelingsprofiel (werkitem 7375).
 *
 * Dezelfde meldingenfamilie als de rest van de app, en geen eigen dialoog: de planner hoeft hier niets te
 * beslissen. Sluiten is de gewone actie van de melding zelf; opnieuw proberen staat erin als extra knop, want
 * dat is bij een tijdelijke storing precies wat er moet gebeuren.
 */
const notifyFailed = (run: PlanRun) => {
  const messageHandler = useMessageHandler();
  const planRunStore = usePlanRunStore();

  // Twee profielen waarbij opnieuw proberen niet zomaar mag maar de aangewezen handeling ís: een tijdelijke
  // storing, en gegevens die tijdens het rekenen wijzigden. In beide gevallen is er niets stukgegaan en is er
  // niets gewijzigd — bij het tweede is de wereld alleen opgeschoven, en dan is opnieuw plannen het antwoord.
  const retryIsTheAnswer = run.failureProfile === PlanRunFailureProfile.Unavailable
    || run.failureProfile === PlanRunFailureProfile.DataChanged;

  const key = failureKeyFor(run);

  messageHandler.showNotification(
    `components.transport.dayplanner.plan_run.${key}.title`,
    `components.transport.dayplanner.plan_run.${key}.description`,
    // Geen fout van de planner en op te lossen door het opnieuw te doen: een waarschuwing. De rest is een
    // echte fout.
    retryIsTheAnswer ? 'warning' : 'error',
    () =>
      h(NButton,
        {
          // Waar opnieuw proberen de aangewezen weg is, staat de knop met nadruk. Bij een fatale fout blijft
          // hij beschikbaar maar rustig: opnieuw proberen presenteren als oplossing zou een belofte zijn die
          // we niet kunnen nakomen.
          text: !retryIsTheAnswer,
          size: 'small',
          type: retryIsTheAnswer ? 'primary' : 'default',
          style: { marginTop: 'var(--dis-space-1_0x)' },
          onClick: () => void planRunStore.retryRun(run),
        },
        { default: () => messageHandler.i18n.t('components.transport.dayplanner.plan_run.retry') }),
    undefined,
    // Blijft staan tot de planner hem wegklikt: een run duurt minuten, en een melding die na zeven tellen
    // vervaagt kan hij zomaar gemist hebben.
    {
      values: {
        reference: planRunReference(run.id),
        transportCount: run.createdTransportCount ?? 0,
      },
      duration: null,
    },
  );
};

/**
 * De sleutel per afhandelingsprofiel.
 *
 * - W: tijdelijk niet beschikbaar; TMS+ heeft het zelf al een paar keer geprobeerd, straks lukt het wel.
 * - T: er kwam geen resultaat; de run is afgebroken en er is niets gewijzigd. Binnen T krijgt één geval een
 *   eigen tekst (werkitem 7456): het vangnet stelde vast dat ónze verwerking stopte terwijl de engine nog
 *   rekende. Voor de planner is de vervolgstap hetzelfde — opnieuw starten — maar "er kwam geen resultaat"
 *   zou hier niet kloppen. En binnen dát geval nog een splitsing (werkitem 7496): stopte de verwerking nadat
 *   ze de ritten al had aangemaakt, dan staan die er wél en is "er is niets gewijzigd" onwaar. Het aantal
 *   aangemaakte ritten is daarvoor het exacte signaal: het wordt in dezelfde transactie vastgelegd als de
 *   ritten zelf, dus staat het er, dan bestaan ze.
 * - V: de aanvraag was niet compleet en is nooit verzonden.
 * - G: de gegevens wijzigden tijdens het rekenen, dus het plan paste niet meer bij de werkelijkheid.
 * - F en al het overige: een neutrale melding met een referentiecode, zonder technische details.
 */
const failureKeyFor = (run: PlanRun): string => {
  switch (run.failureProfile) {
    case PlanRunFailureProfile.Unavailable:
      return 'unavailable';
    case PlanRunFailureProfile.TimedOut:
      if (!run.processGone) {
        // Elk pad naar deze tekst ligt vóór het aanmaken van de ritten: de poll-deadline, een resultaat dat te
        // laat kwam om nog verwerkt te worden, een aanvraag die nooit aankwam, en het vangnet dat afsluit
        // terwijl de run nog niet aan het verwerken was. "Er is niets gewijzigd" klopt daar dus.
        return 'timed_out';
      }

      return (run.createdTransportCount ?? 0) > 0 ? 'process_gone_applied' : 'process_gone';
    case PlanRunFailureProfile.Rejected:
      return 'rejected';
    case PlanRunFailureProfile.DataChanged:
      return 'data_changed';
    default:
      return 'fatal';
  }
};

/**
 * De korte melding, voor de gevallen die geen uitleg per traject vragen: een volledig geslaagde run, of een run
 * die niets kon plannen zonder dat er iets mis was.
 */
const notifyFinished = (run: PlanRun) => {
  const messageHandler = useMessageHandler();

  const transportCount = run.createdTransportCount ?? 0;
  const unplannedCount = run.unplannedTrajectoryIds.length;
  const trajectoryCount = run.trajectoryIds.length - unplannedCount;

  let key: string;
  let type: string;

  if (transportCount === 0) {
    key = 'nothing_planned';
    type = 'warning';
  } else if (unplannedCount > 0) {
    key = 'partial';
    type = 'warning';
  } else {
    key = 'completed';
    type = 'success';
  }

  // useMessageHandler en niet naive's useNotification(): deze tick loopt buiten een componentcontext.
  // Alles behalve een volledig geslaagde run blijft staan tot de planner hem wegklikt. Een run duurt al
  // gauw enkele minuten; een melding die na zeven tellen vervaagt vertelt hem nooit dat er auto's op
  // Te plannen zijn blijven staan — dan lijkt het alsof APE de boel stilletjes leeg heeft teruggegeven.
  messageHandler.showNotification(
    `components.transport.dayplanner.plan_run.${key}.title`,
    `components.transport.dayplanner.plan_run.${key}.description`,
    type,
    undefined,
    undefined,
    {
      values: { transportCount, trajectoryCount, unplannedCount, reference: planRunReference(run.id) },
      duration: type === 'success' ? undefined : null,
    },
  );
};


/**
 * Hoe lang na afloop de uitkomst van een run nog getoond wordt aan de planner die hem startte.
 *
 * Zonder deze inhaalslag mist hij de terugkoppeling zodra hij het tabblad ververst of even wegklikt op het
 * verkeerde moment: de melding hangt anders volledig aan het tabblad dat de run zag aflopen. De bevestiging
 * per run zorgt dat hij hem daarna niet nóg een keer krijgt.
 *
 * Eén werkdag, want dat is de horizon waarop een planner met deze trajecten bezig is; een run van vorige week
 * hoort niet ineens weer op te komen.
 */
const CATCH_UP_WINDOW_MS = 8 * 60 * 60 * 1000;

/** Een run die zojuist afliep zonder dat dit tabblad hem actief heeft zien staan. */
const justMissedTheEnd = (run: PlanRun): boolean =>
  run.startedByMe
  && run.completedAt !== null
  && Date.now() - run.completedAt < CATCH_UP_WINDOW_MS
  && !usePlanRunStore().hasAcknowledged(run.id);

const tick = async () => {
  timer = null;

  const planRunStore = usePlanRunStore();

  const authStore = useAuthStore();

  try {
    // Wachten op de hydratatie, niet alleen de stand lezen: bij een koude load loopt dit rondje vóór de
    // hydratatie, en dan zou het eerste rondje altijd overgeslagen worden en de stand pas na het volgende
    // interval verschijnen. De opvraging dedupliceert en cachet, dus dit kost na de eerste keer niets.
    await authStore.ensureHydrated();

    // Een achtergrondtab hoeft niets op te vragen; bij terugkeer haalt onVisibilityChange het meteen op.
    // De authenticatiecheck hoort erbij sinds de poller app-breed draait: op het inlogscherm zou hij anders
    // elke twintig seconden een 401 ophalen.
    //
    // needsPlanRunLock en niet canSeePlanRun: de vergrendeling raakt ook opdrachten, voertuigen en
    // transportvoertuigen, dus wie daarvan iets mag bewerken heeft de stand nodig — ook zonder recht op de
    // voortgangsweergave. Die weergave blijft zelf op canSeePlanRun zitten (zie showProgressPanel), dus een
    // opdrachtbehandelaar ziet wél de sloten en níet de voortgang van de run.
    if (!document.hidden && authStore.isAuthenticated() && planRunStore.needsPlanRunLock) {
      const run = await planRunStore.fetchLatestRun();

      // undefined = opvraging mislukt: stand ongemoeid laten en het volgende rondje afwachten.
      if (run !== undefined) {
        if (planRunStore.isPlanRunActive) {
          trackedRunId = run?.id ?? null;
        } else if (run !== null && (trackedRunId === run.id || justMissedTheEnd(run))) {
          const wasWatched = trackedRunId === run.id;
          trackedRunId = null;
          await handleFinished(run, wasWatched);
        }
      }
    }
  } catch (error) {
    // Eén mislukt rondje mag de poller niet stilleggen: zonder deze vangst wordt schedule() hieronder
    // nooit bereikt en blijft de dagplanner de rest van de sessie op zijn placeholders staan.
    console.error('Plan-run polling mislukte; volgende rondje wordt gewoon ingepland.', error);
  }

  if (subscribers > 0) {
    schedule();
  }
};

const schedule = () => {
  stopTimer();
  timer = setTimeout(() => void tick(), usePlanRunStore().isPlanRunActive ? ACTIVE_POLL_MS : IDLE_POLL_MS);
};

const onVisibilityChange = () => {
  if (!document.hidden && subscribers > 0) {
    void tick();
  }
};

/**
 * Houdt de plan-run-stand bij zolang de aanroepende component gemount is. Gekoppelde setTimeout in plaats
 * van setInterval: bij een langzaam antwoord mogen opvragingen niet op elkaar stapelen, en het interval
 * hangt af van de huidige stand.
 *
 * @param options.refreshDayPlanner De aanroeper toont het dagplanner-overzicht, en dat mag na een afgeronde
 *   run herladen worden. Alleen de dagplanner zelf zet dit aan.
 */
export function usePlanRunPolling(options?: { refreshDayPlanner?: boolean }) {
  const refreshDayPlanner = options?.refreshDayPlanner === true;

  onMounted(() => {
    subscribers++;

    if (refreshDayPlanner) {
      dayPlannerSubscribers++;
    }

    if (subscribers === 1) {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    // Direct één opvraging: zo verschijnt een run die een collega startte meteen. Ook wanneer de app-brede
    // afnemer er al staat en de dagplanner erbij komt: die moet zijn placeholders nú hebben en niet pas na
    // het volgende interval. tick() plant zelf het vervolg in en ruimt de lopende timer op, dus dit levert
    // geen tweede reeks opvragingen op.
    if (subscribers === 1 || refreshDayPlanner) {
      void tick();
    }
  });

  onBeforeUnmount(() => {
    subscribers--;

    if (refreshDayPlanner) {
      dayPlannerSubscribers--;
    }

    if (subscribers === 0) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopTimer();
      trackedRunId = null;
    }
  });
}
